import { useMemo, useState } from 'react';
import {
  PieChart as RePieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  ReferenceLine,
  LabelList,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  RadialBar,
  RadialBarChart,
  Legend
} from 'recharts';
import { Calendar, DollarSign, Store, Tag, TrendingDown, TrendingUp, Wallet as WalletIcon, ChevronLeft, ChevronRight, RotateCcw, ChevronDown, Info, ShieldCheck, Activity, Zap, Layers, PieChart } from 'lucide-react';
import { getCurrentRomeTime, formatCurrency } from '../utils/formatters';
import { format, startOfWeek, startOfMonth, startOfYear, addWeeks, addMonths, addYears, addDays, subWeeks, subMonths, subYears, eachDayOfInterval, differenceInCalendarDays, eachWeekOfInterval, eachMonthOfInterval, endOfWeek, endOfMonth } from 'date-fns';
import { it } from 'date-fns/locale';
import { getTotal, getByCategory, getByStore, getMonthlyData, getDailyAvgHeatmap, getBalanceComparison, getDailyTrend, getNetWorthTrend, getInvestmentTrend, getBreakdown, filterItems, getRadarData, getFinancialIndices } from '../features/statistics/statisticsLogic';
import { calculateNextOccurrenceDate } from '../features/recurring/recurringLogic';
import { useTheme } from '../hooks/useTheme';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B6B', '#6366f1', '#f59e42'];
const DISTRIBUTION_COLORS = ['#6366f1', '#0ea5e9', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#a855f7'];
const CREDIT_COLOR = '#16a34a';
const DEBT_COLOR = '#ef4444';
const WEEK_DAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

const getYAxisTicks = (data, keys) => {
  if (!data || !Array.isArray(data) || data.length === 0) return undefined;
  let maxVal = 0;
  data.forEach(d => {
    if (!d) return;
    keys.forEach(k => {
      const val = Number(d[k]);
      if (Number.isFinite(val) && val > maxVal) {
        maxVal = val;
      }
    });
  });
  if (maxVal <= 0) return [0, 250, 500];
  
  let interval = 1000;
  if (maxVal > 2000) {
    while (maxVal / interval > 5) {
      interval *= 2;
    }
  } else {
    while (maxVal / interval < 3 && interval > 1) {
      if (interval === 125) {
        interval = 50;
      } else if (interval === 50) {
        interval = 25;
      } else if (interval === 25) {
        interval = 10;
      } else if (interval === 10) {
        interval = 5;
      } else if (interval === 5) {
        interval = 1;
      } else {
        interval = interval / 2;
      }
    }
  }
  
  const ticks = [];
  const limit = Math.floor(maxVal / interval) * interval + interval;
  for (let val = 0; val <= limit; val += interval) {
    ticks.push(val);
  }
  return ticks;
};

const getAxisConfig = (data, keys) => {
  const ticks = getYAxisTicks(data, keys);
  if (!ticks) return {};
  return {
    ticks,
    domain: [0, ticks[ticks.length - 1]]
  };
};

function Statistics({
  expenses = [],
  incomes = [],
  wallets = [],
  debts = [],
  categories = [],
  recurringTransactions = [],
  statisticsPreferences = {},
  onStatisticsPreferencesChange,
  activeFilters = {}
}) {
  const { theme } = useTheme();

  const [selectedFinancialIndex, setSelectedFinancialIndex] = useState(null);


  const FinancialIndexCard = ({ id, index, icon: Icon, onClick }) => {
    return (
      <div
        onClick={() => onClick(id)}
        className="card p-2.5 sm:p-3 flex items-center gap-3 cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden group"
      >
        <div className="absolute top-0 right-0 w-8 h-8 bg-current opacity-[0.03] rounded-bl-full group-hover:opacity-[0.06] transition-opacity"></div>
        <div 
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 shadow-sm"
          style={{ backgroundColor: `${index?.color}15`, color: index?.color }}
        >
          {Icon && <Icon className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase font-bold tracking-wider text-gray-500 truncate mb-0.5">{index?.label}</div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-base font-black text-gray-900 dark:text-gray-100 leading-none">{index?.score}</span>
            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-md truncate lg:hidden" style={{ backgroundColor: `${index?.color}20`, color: index?.color }}>
              {index?.status}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const FinancialIndexDetail = ({ id, index, onClose, icon: Icon }) => {
    if (!index) return null;
    return (
      <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={onClose}></div>
        <div className="card w-full max-w-sm relative z-10 animate-in fade-in zoom-in duration-300 p-6 overflow-hidden">
          <div className="absolute top-0 right-0 p-4">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              <RotateCcw className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <div 
              className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg"
              style={{ background: `linear-gradient(135deg, ${index.color}, ${index.color}dd)`, color: 'white' }}
            >
              {Icon && <Icon className="w-8 h-8" />}
            </div>
            <div>
              <h3 className="text-xl font-black text-gray-900 dark:text-gray-100">{index.label}</h3>
              <div className="text-sm font-bold" style={{ color: index.color }}>{index.status} • Punteggio {index.score}/100</div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
              <div className="text-xs uppercase font-bold tracking-widest text-gray-400 mb-1">Valore Attuale</div>
              <div className="text-2xl font-black text-gray-900 dark:text-gray-100">{index.value}</div>
            </div>

            <div className="p-4 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
              <div className="text-xs uppercase font-bold tracking-widest text-gray-400 mb-2 flex items-center gap-1">
                <Info className="w-3 h-3" />
                Calcolo e Dettagli
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed italic">
                "{index.details}"
              </p>
            </div>

            <div className="bg-emerald-50 dark:bg-emerald-900/10 p-4 rounded-xl border border-emerald-100 dark:border-emerald-800/30">
              <p className="text-sm text-emerald-800 dark:text-emerald-300 font-medium">
                {index.comment}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-full mt-6 py-3 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 font-bold rounded-xl transition-transform active:scale-95 shadow-xl"
          >
            Capito
          </button>
        </div>
      </div>
    );
  };


  // 1. FILTRA I DATI in base ad activeFilters (ignorando il tempo)
  // Dobbiamo iniettare 'type' affinché il filtro transactionType funzioni correttamente se vogliamo supportarlo,
  // O filtriamo semplicemente per Categoria/Negozio/Portafoglio, che di solito è sufficiente.
  // Il filtro globale `transactionType` ('all', 'expenses', 'incomes') dovrebbe idealmente attivare la visibilità.
  // Se transactionType === 'expenses', filteredIncomes dovrebbe essere vuoto.

  const filteredExpenses = useMemo(() => {
    // Se il filtro globale è 'incomes', le spese dovrebbero essere vuote
    if (activeFilters.transactionType === 'incomes') return [];
    return filterItems(expenses, activeFilters);
  }, [expenses, activeFilters]);

  const filteredIncomes = useMemo(() => {
    // Se il filtro globale è 'expenses', le entrate dovrebbero essere vuote
    if (activeFilters.transactionType === 'expenses') return [];
    return filterItems(incomes, activeFilters);
  }, [incomes, activeFilters]);

  const filteredDebts = useMemo(() => {
    // I debiti potrebbero non seguire rigorosamente la stessa struttura categoria/negozio, ma proviamoci
    return filterItems(debts, activeFilters);
  }, [debts, activeFilters]);

  const filteredRecurring = useMemo(() => {
    // Filtra anche le transazioni ricorrenti in modo che le proiezioni rispettino i filtri
    // Includiamo i trasferimenti nelle basi per permettere alla Gestione Liquidità di proiettare gli investimenti
    const filtered = filterItems(recurringTransactions, { ...activeFilters, showTransfers: true });
    // Rispettiamo anche transactionType
    if (activeFilters.transactionType === 'expenses') return filtered.filter(t => t.type === 'expense');
    if (activeFilters.transactionType === 'incomes') return filtered.filter(t => t.type === 'income');
    return filtered;
  }, [recurringTransactions, activeFilters]);

  const fullData = useMemo(() => ({ expenses: filteredExpenses, incomes: filteredIncomes }), [filteredExpenses, filteredIncomes]);
  const [comparisonOffset, setComparisonOffset] = useState(0);
  const [activeSection, setActiveSection] = useState('general');
  const [openRangeMenu, setOpenRangeMenu] = useState(null);

  const {
    comparisonRange: comparisonRangePreference = 'week',
  } = statisticsPreferences || {};

  const sanitizedComparisonRange = ['week', 'month', 'year', 'custom'].includes(comparisonRangePreference)
    ? comparisonRangePreference
    : (comparisonRangePreference === 'custom' ? 'custom' : 'week');

  const handleComparisonRangeChange = (value) => {
    if (sanitizedComparisonRange !== value) {
      onStatisticsPreferencesChange?.({ comparisonRange: value });
    }
  };

  const referenceDate = useMemo(() => {
    const now = getCurrentRomeTime();
    if (sanitizedComparisonRange === 'year') {
      return addYears(now, comparisonOffset);
    }
    if (sanitizedComparisonRange === 'month') {
      return addMonths(now, comparisonOffset);
    }
    return addWeeks(now, comparisonOffset);
  }, [sanitizedComparisonRange, comparisonOffset]);

  const buildPeriodWindow = (range, dateAnchor) => {
    const now = dateAnchor || getCurrentRomeTime();
    if (range === 'year') {
      const currentStart = startOfYear(now);
      return {
        currentStart,
        currentEnd: addYears(currentStart, 1),
        previousStart: subYears(currentStart, 1),
        previousEnd: currentStart,
      };
    }
    if (range === 'month') {
      const currentStart = startOfMonth(now);
      return {
        currentStart,
        currentEnd: addMonths(currentStart, 1),
        previousStart: subMonths(currentStart, 1),
        previousEnd: currentStart,
      };
    }
    if (range === 'custom') {
      const customStart = statisticsPreferences.customStartDate ? new Date(statisticsPreferences.customStartDate) : startOfMonth(now);
      const baseEnd = statisticsPreferences.customEndDate ? new Date(statisticsPreferences.customEndDate) : addMonths(customStart, 1);
      const customEnd = addDays(baseEnd, 1); // Rendilo esclusivo (giorno successivo) per la logica del filtro
      
      // Calcola il periodo precedente della stessa durata
      const durationMs = customEnd.getTime() - customStart.getTime();
      const previousStart = new Date(customStart.getTime() - durationMs);
      const previousEnd = customStart;

      return {
        currentStart: customStart,
        currentEnd: customEnd,
        previousStart,
        previousEnd,
      };
    }
    const currentStart = startOfWeek(now, { weekStartsOn: 1 });
    return {
      currentStart,
      currentEnd: addWeeks(currentStart, 1),
      previousStart: subWeeks(currentStart, 1),
      previousEnd: currentStart,
    };
  };

  const sumInRange = (items, start, end) => {
    if (!Array.isArray(items)) {
      return 0;
    }
    return items.reduce((total, item) => {
      if (!item?.date || item.category === 'Trasferimento') {
        return total;
      }
      const date = new Date(item.date);
      if (Number.isNaN(date.getTime()) || date < start || date >= end) {
        return total;
      }
      const amount = Number.parseFloat(item.amount);
      return Number.isFinite(amount) ? total + amount : total;
    }, 0);
  };



  const periodWindow = useMemo(
    () => {
      return buildPeriodWindow(sanitizedComparisonRange, referenceDate);
    },
    [sanitizedComparisonRange, referenceDate, statisticsPreferences],
  );

  // PROIETTA LE TRANSAZIONI RICORRENTI
  const projectedTransactions = useMemo(() => {
    if (!recurringTransactions || recurringTransactions.length === 0 || !periodWindow || !periodWindow.currentStart || !periodWindow.currentEnd) {
      return { expenses: [], incomes: [] };
    }

    const projectedExpenses = [];
    const projectedIncomes = [];
    const { currentStart, currentEnd } = periodWindow;

    filteredRecurring.forEach(recurring => {
      if (recurring.active === false) return;

      // Assicurati di avere un punto di partenza valido
      let pointerDate;
      try {
        pointerDate = new Date(recurring.nextOccurrence);
        if (Number.isNaN(pointerDate.getTime())) return;
      } catch (e) {
        return;
      }

      // Se la prossima occorrenza è già oltre la finestra, saltiamo
      if (pointerDate > currentEnd) return;

      // Limita le iterazioni per evitare loop infiniti (circa 1 anno di proiezione max)
      let safetyCounter = 0;
      const MAX_ITERATIONS = 365;

      // Se pointerDate è prima di currentStart, dobbiamo avanzarlo finché non rientra nella finestra o la supera
      while (pointerDate < currentStart && safetyCounter < MAX_ITERATIONS) {
        const nextDate = calculateNextOccurrenceDate(pointerDate, recurring.frequency);
        if (!nextDate) break; // Non dovrebbe accadere se la frequenza è valida
        pointerDate = nextDate;
        safetyCounter++;
      }

      // Ora raccogli le occorrenze all'interno della finestra
      while (pointerDate < currentEnd && safetyCounter < MAX_ITERATIONS) {
        // È all'interno della finestra [currentStart, currentEnd)
        if (pointerDate >= currentStart) {
          const transaction = {
            id: `proj_${recurring.id}_${pointerDate.getTime()}`,
            amount: recurring.amount,
            date: pointerDate.toISOString(),
            category: recurring.category,
            store: recurring.store,
            type: recurring.type,
            walletId: recurring.walletId,
            isProjected: true
          };

          if (recurring.type === 'income') {
            projectedIncomes.push(transaction);
          } else {
            projectedExpenses.push(transaction);
          }
        }

        const nextDate = calculateNextOccurrenceDate(pointerDate, recurring.frequency);
        if (!nextDate) break;
        pointerDate = nextDate;
        safetyCounter++;
      }
    });

    return { expenses: projectedExpenses, incomes: projectedIncomes };
  }, [filteredRecurring, periodWindow]);

  const overviewComparison = useMemo(() => {
    const { currentStart, currentEnd, previousStart, previousEnd } = buildPeriodWindow(
      sanitizedComparisonRange,
      referenceDate,
    );
    const currentIncome = sumInRange(fullData.incomes, currentStart, currentEnd);
    const currentExpense = sumInRange(fullData.expenses, currentStart, currentEnd);
    const previousIncome = sumInRange(fullData.incomes, previousStart, previousEnd);
    const previousExpense = sumInRange(fullData.expenses, previousStart, previousEnd);

    // Aggiungi i totali proiettati (escludendo i trasferimenti per coerenza con i dati reali)
    const projectedIncomeTotal = projectedTransactions.incomes
      .filter(t => t.category !== 'Trasferimento')
      .reduce((acc, curr) => acc + Number(curr.amount), 0);
    const projectedExpenseTotal = projectedTransactions.expenses
      .filter(t => t.category !== 'Trasferimento')
      .reduce((acc, curr) => acc + Number(curr.amount), 0);

    return {
      current: {
        income: currentIncome + projectedIncomeTotal,
        expense: currentExpense + projectedExpenseTotal,
        balance: (currentIncome + projectedIncomeTotal) - (currentExpense + projectedExpenseTotal),
      },
      previous: {
        income: previousIncome,
        expense: previousExpense,
        balance: previousIncome - previousExpense,
      },
    };
  }, [fullData, sanitizedComparisonRange, referenceDate, projectedTransactions]);

  const periodExpenses = useMemo(() => {
    const { currentStart, currentEnd } = periodWindow || {};
    if (!currentStart || !currentEnd) return fullData.expenses;
    const actual = (fullData.expenses || []).filter((item) => {
      if (!item?.date) return false;
      const date = new Date(item.date);
      if (Number.isNaN(date.getTime())) return false;
      return date >= currentStart && date < currentEnd;
    });
    return [...actual, ...projectedTransactions.expenses];
  }, [fullData, periodWindow, projectedTransactions.expenses]);

  const periodIncomes = useMemo(() => {
    const { currentStart, currentEnd } = periodWindow || {};
    if (!currentStart || !currentEnd) return fullData.incomes;
    const actual = (fullData.incomes || []).filter((item) => {
      if (!item?.date) return false;
      const date = new Date(item.date);
      if (Number.isNaN(date.getTime())) return false;
      return date >= currentStart && date < currentEnd;
    });
    return [...actual, ...projectedTransactions.incomes];
  }, [fullData, periodWindow, projectedTransactions.incomes]);

  const periodDebts = useMemo(() => {
    const { currentStart, currentEnd } = periodWindow || {};
    if (!currentStart || !currentEnd) return filteredDebts;
    return (filteredDebts || []).filter((item) => {
      if (!item?.date) return false;
      const date = new Date(item.date);
      if (Number.isNaN(date.getTime())) return false;
      return date >= currentStart && date < currentEnd;
    });
  }, [filteredDebts, periodWindow]);

  const periodData = useMemo(() => ({ expenses: periodExpenses, incomes: periodIncomes, debts: periodDebts }), [periodExpenses, periodIncomes, periodDebts]);

  const incomeBreakdown = useMemo(
    () => {
      // Logica modificata: fullData include già i debiti, quindi periodIncomes include i debiti efficacemente (filtrati per tempo)
      // Passiamo null per i debiti poiché getBreakdown ora si basa sulla presenza di personName nell'array unito
      return getBreakdown(periodData.incomes, null, 'income');
    },
    [periodData]
  );

  const expenseBreakdown = useMemo(
    () => {
      return getBreakdown(periodData.expenses, null, 'expense');
    },
    [periodData]
  );

  const formatPeriodLabel = (range, start, end) => {
    if (!start || !end) return '';
    if (range === 'year') {
      return format(start, 'yyyy', { locale: it });
    }
    if (range === 'month') {
      return format(start, 'LLLL yyyy', { locale: it });
    }
    if (range === 'custom') {
      const inclusiveEnd = new Date(end.getTime() - 24 * 60 * 60 * 1000);
      return `${format(start, 'd LLL yyyy', { locale: it })} → ${format(inclusiveEnd, 'd LLL yyyy', { locale: it })}`;
    }
    const endDate = new Date(end.getTime() - 24 * 60 * 60 * 1000);
    return `${format(start, 'd LLL', { locale: it })} → ${format(endDate, 'd LLL', { locale: it })}`;
  };

  const periodLabel = formatPeriodLabel(
    sanitizedComparisonRange,
    periodWindow.currentStart,
    periodWindow.currentEnd,
  );

  const periodTrendLine = useMemo(() => {
    return getDailyTrend(periodData.expenses, periodData.incomes, periodWindow.currentStart, periodWindow.currentEnd);
  }, [periodData.expenses, periodData.incomes, periodWindow.currentStart, periodWindow.currentEnd]);

  // adaptiveTrendData: aggrega i dati in bucket giornalieri, settimanali o mensili
  const adaptiveTrendData = useMemo(() => {
    if (!periodWindow.currentStart || !periodWindow.currentEnd) return { type: 'daily', data: [] };

    const diffDays = differenceInCalendarDays(periodWindow.currentEnd, periodWindow.currentStart);
    const isCustom = sanitizedComparisonRange === 'custom';

    if (isCustom && diffDays > 90) {
      // Aggrega per Mese
      const intervals = eachMonthOfInterval({
        start: periodWindow.currentStart,
        end: new Date(periodWindow.currentEnd.getTime() - 1),
      });

      const data = intervals.map(monthStart => {
        const monthEnd = endOfMonth(monthStart);
        const label = format(monthStart, "MMM ''yy", { locale: it });
        
        const expenses = (periodData.expenses || []).filter(e => {
          const d = new Date(e.date);
          return d >= monthStart && d <= monthEnd;
        }).reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

        const incomes = (periodData.incomes || []).filter(i => {
          const d = new Date(i.date);
          return d >= monthStart && d <= monthEnd;
        }).reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);

        return { label, Spese: expenses, Entrate: incomes };
      });

      return { type: 'bar', data };
    }

    if (isCustom && diffDays > 60) {
      // Aggrega per Settimana
      const intervals = eachWeekOfInterval({
        start: periodWindow.currentStart,
        end: new Date(periodWindow.currentEnd.getTime() - 1),
      }, { weekStartsOn: 1 });

      const data = intervals.map(weekStart => {
        const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
        const label = format(weekStart, "d MMM", { locale: it });

        const expenses = (periodData.expenses || []).filter(e => {
          const d = new Date(e.date);
          return d >= weekStart && d <= weekEnd;
        }).reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

        const incomes = (periodData.incomes || []).filter(i => {
          const d = new Date(i.date);
          return d >= weekStart && d <= weekEnd;
        }).reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);

        return { label, Spese: expenses, Entrate: incomes };
      });

      return { type: 'bar', data };
    }

    // Default: Giornaliero (AreaChart)
    return { type: 'area', data: periodTrendLine };
  }, [periodWindow, periodData, periodTrendLine, sanitizedComparisonRange]);


  const getReferenceByOffset = (offset) => {
    const now = getCurrentRomeTime();
    if (sanitizedComparisonRange === 'year') return addYears(now, offset);
    if (sanitizedComparisonRange === 'month') return addMonths(now, offset);
    if (sanitizedComparisonRange === 'custom') return now;
    return addWeeks(now, offset);
  };

  const canMoveToOffset = (candidateOffset) => {
    const candidateDate = getReferenceByOffset(candidateOffset);
    const candidateWindow = buildPeriodWindow(sanitizedComparisonRange, candidateDate);
    const now = getCurrentRomeTime();
    return candidateWindow.currentStart <= now;
  };

  const canGoForward = useMemo(
    () => canMoveToOffset(comparisonOffset + 1),
    [comparisonOffset, sanitizedComparisonRange],
  );

  const handleOffsetChange = (delta) => {
    setComparisonOffset((prev) => {
      const next = prev + delta;
      if (!canMoveToOffset(next)) {
        return prev;
      }
      return next;
    });
  };

  const resetOffset = () => {
    setComparisonOffset(0);
  };

  const formatCurrencyShort = (amount) => {
    const numeric = Number.isFinite(amount) ? amount : 0;
    const rounded = Math.round(numeric);
    return `€${rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'")}`;
  };

  const renderRangeControls = (anchorKey) => (
    <div className="flex items-center gap-1.5 px-1 py-1 bg-white/40 dark:bg-gray-900/40 backdrop-blur-md rounded-2xl border border-gray-200/50 dark:border-gray-700/50">
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpenRangeMenu((prev) => (prev === anchorKey ? null : anchorKey))}
          className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 text-sm font-semibold text-gray-800 dark:text-gray-100 px-3 py-2 rounded-lg border border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-haspopup="listbox"
          aria-expanded={openRangeMenu === anchorKey}
          aria-label="Seleziona periodo"
        >
          {sanitizedComparisonRange === 'week' ? 'S' : 
            sanitizedComparisonRange === 'month' ? 'M' : 
            sanitizedComparisonRange === 'year' ? 'A' : 'P'}
          <ChevronDown className="w-4 h-4 text-gray-500" />
        </button>
        {openRangeMenu === anchorKey && (
          <div className="absolute left-0 mt-1 w-32 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-30">
            {[
              { key: 'week', label: 'Settimana' },
              { key: 'month', label: 'Mese' },
              { key: 'year', label: 'Anno' },
              { key: 'custom', label: 'Personalizzato' },
            ].map((option) => (
              <button
                key={option.key}
                className={`w-full text-left px-3 py-2 text-sm ${sanitizedComparisonRange === option.key
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200'
                  : 'text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                onClick={() => {
                  handleComparisonRangeChange(option.key);
                  resetOffset();
                  setOpenRangeMenu(null);
                }}
                role="option"
                aria-selected={sanitizedComparisonRange === option.key}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center flex-1 justify-center gap-2">
        {sanitizedComparisonRange === 'custom' ? (
          <div className="flex items-center gap-2">
            <input
              type="date"
              className="bg-transparent border-none text-sm font-semibold focus:ring-0 p-0 w-28"
              value={statisticsPreferences.customStartDate || format(periodWindow.currentStart, 'yyyy-MM-dd')}
              onChange={(e) => onStatisticsPreferencesChange?.({ customStartDate: e.target.value })}
            />
            <span className="text-gray-400">→</span>
            <input
              type="date"
              className="bg-transparent border-none text-sm font-semibold focus:ring-0 p-0 w-28"
              value={statisticsPreferences.customEndDate || format(new Date(periodWindow.currentEnd.getTime() - 86400000), 'yyyy-MM-dd')}
              onChange={(e) => onStatisticsPreferencesChange?.({ customEndDate: e.target.value })}
            />
          </div>
        ) : (
          <>
            <button
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              onClick={() => handleOffsetChange(-1)}
              aria-label="Periodo precedente"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div
              className="flex-1 text-center text-sm font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap select-none"
            >
              {periodLabel || 'Periodo attuale'}
            </div>
            <button
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handleOffsetChange(1)}
              disabled={!canGoForward}
              aria-label="Periodo successivo"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {sanitizedComparisonRange !== 'custom' && (
        <button
          className="p-2 rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors"
          onClick={resetOffset}
          aria-label="Torna a oggi"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  const { totalExpenses, totalIncomes } = useMemo(
    () => getTotal(periodData.expenses, periodData.incomes),
    [periodData],
  );

  const monthlyData = useMemo(
    () => getMonthlyData(periodData.expenses, periodData.incomes),
    [periodData],
  );

  const yearlyChartData = useMemo(() => {
    if (sanitizedComparisonRange !== 'year') return [];
    if (!periodWindow?.currentStart) return [];

    const stats = [];
    const yearStart = startOfYear(periodWindow.currentStart);

    for (let i = 0; i < 12; i++) {
      const currentDate = addMonths(yearStart, i);
      const key = format(currentDate, 'yyyy-MM');
      const dataForMonth = monthlyData.find((m) => m.month === key);

      stats.push({
        label: format(currentDate, 'MMM', { locale: it }),
        fullLabel: format(currentDate, 'MMMM yyyy', { locale: it }),
        Entrate: dataForMonth ? dataForMonth.Entrate : 0,
        Spese: dataForMonth ? dataForMonth.Spese : 0,
      });
    }
    return stats;
  }, [sanitizedComparisonRange, periodWindow, monthlyData]);

  const expenseByCategory = useMemo(() => getByCategory(periodData.expenses), [periodData]);
  const incomeByCategory = useMemo(() => getByCategory(periodData.incomes), [periodData]);

  // Helper per condensare i dati in Top N + "Resto"
  const condenseChartData = (data, topN = 7) => {
    if (!data || data.length <= topN) return data;
    const top = data.slice(0, topN);
    const rest = data.slice(topN);
    const restValue = rest.reduce((sum, item) => sum + item.value, 0);
    if (restValue > 0) {
      top.push({ name: 'Resto', value: restValue });
    }
    return top;
  };

  const expenseCategoryChart = useMemo(
    () => {
      const sorted = Object.entries(expenseByCategory)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);
      return condenseChartData(sorted, 7);
    },
    [expenseByCategory],
  );

  const incomeCategoryChart = useMemo(
    () => {
      const sorted = Object.entries(incomeByCategory)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);
      return condenseChartData(sorted, 7);
    },
    [incomeByCategory],
  );

  const expenseByStore = useMemo(() => getByStore(periodData.expenses), [periodData]);
  const incomeByStore = useMemo(() => getByStore(periodData.incomes), [periodData]);

  const expenseStoreChart = useMemo(() => {
    const sorted = Object.entries(expenseByStore)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
    return condenseChartData(sorted, 7);
  }, [expenseByStore]);

  const incomeStoreChart = useMemo(() => {
    const sorted = Object.entries(incomeByStore)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
    return condenseChartData(sorted, 7);
  }, [incomeByStore]);

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, value, x, y }) => {
    return (
      <text
        x={x}
        y={y}
        fill={theme === 'dark' ? '#e5e7eb' : '#374151'}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={10}
      >
        {`${name} (${formatCurrencyShort(value)})`}
      </text>
    );
  };

  const dailyAvgHeatmap = useMemo(
    () => getDailyAvgHeatmap(periodData.expenses, periodData.incomes),
    [periodData],
  );

  const {
    expenses: weeklyExpenseAverages = [],
    incomes: weeklyIncomeAverages = [],
    expensesMean: averageDailyExpenseValue = 0,
    incomesMean: averageDailyIncomeValue = 0,
  } = dailyAvgHeatmap || {};

  const weeklyAverageChartData = WEEK_DAYS.map((day, index) => ({
    day,
    spese: weeklyExpenseAverages[index] || 0,
    entrate: weeklyIncomeAverages[index] || 0,
  }));

  const incomeComparisonData = useMemo(
    () => getBalanceComparison(
      fullData.expenses, 
      fullData.incomes, 
      sanitizedComparisonRange, 
      'income', 
      referenceDate,
      { customDates: sanitizedComparisonRange === 'custom' ? periodWindow : null }
    ),
    [fullData, sanitizedComparisonRange, referenceDate, periodWindow],
  );

  const expenseComparisonData = useMemo(
    () => getBalanceComparison(
      fullData.expenses, 
      fullData.incomes, 
      sanitizedComparisonRange, 
      'expense', 
      referenceDate,
      { customDates: sanitizedComparisonRange === 'custom' ? periodWindow : null }
    ),
    [fullData, sanitizedComparisonRange, referenceDate, periodWindow],
  );

  const netWorthDistribution = useMemo(() => {
    const safeWallets = Array.isArray(wallets) ? wallets : [];
    const walletCategories = categories?.wallet || [];

    // Raggruppa per TIPO di portafoglio (Categoria)
    // Mappa: NomeTipo -> { totale: numero, colore: stringa }
    const typeGroups = {};

    safeWallets
      .filter((wallet) => !wallet?.isVirtual)
      .forEach((wallet) => {
        const amount = Number.parseFloat(wallet.balance) || 0;
        const typeName = wallet.type || 'Altro';

        if (!typeGroups[typeName]) {
          // Trova il colore della categoria
          const cat = walletCategories.find(c => c.name === typeName) || {};
          // Colore predefinito se non trovato (es. grigio o uno di quelli predefiniti)
          const color = cat.color || '#9ca3af';
          typeGroups[typeName] = { total: 0, color };
        }

        typeGroups[typeName].total += amount;
      });

    // Calcola Debiti e Crediti
    const personBalances = (() => {
      const acc = {};
      if (!Array.isArray(debts)) return acc;
      debts.forEach((debt) => {
        const personName = debt?.personName;
        const amount = Number.parseFloat(debt?.amount);
        if (!personName || !Number.isFinite(amount)) return;
        const delta = debt.type === 'credit' ? amount : -amount;
        acc[personName] = (acc[personName] || 0) + delta;
      });
      return acc;
    })();

    const creditTotal = Object.values(personBalances)
      .filter((value) => value > 0.01)
      .reduce((sum, value) => sum + value, 0);

    const debtTotal = Object.values(personBalances)
      .filter((value) => value < -0.01)
      .reduce((sum, value) => sum + Math.abs(value), 0);

    const walletTotal = Object.values(typeGroups).reduce((sum, group) => sum + group.total, 0);
    const netWorth = walletTotal + creditTotal - debtTotal;

    // Costruisci i dati del grafico
    const distribution = [
      // Portafogli per Categoria
      ...Object.entries(typeGroups).map(([name, group]) => ({
        name,
        value: group.total,
        chartValue: Math.abs(group.total),
        kind: 'wallet',
        color: group.color, // CUSTOM COLOR
        tone: group.total >= 0 ? 'positive' : 'negative',
      })),
      // Crediti (VERDE)
      {
        name: 'Crediti',
        value: creditTotal,
        chartValue: creditTotal,
        kind: 'credit',
        color: '#22c55e', // GREEN-500
        tone: 'positive',
      },
      // Debiti (ROSSO)
      {
        name: 'Debiti',
        value: -debtTotal,
        chartValue: debtTotal,
        kind: 'debt',
        color: '#ef4444', // RED-500
        tone: 'negative',
      },
    ].filter((entry) => entry.chartValue > 0.01);

    const magnitude = distribution.reduce((sum, entry) => sum + entry.chartValue, 0);

    return {
      walletTotal,
      creditTotal,
      debtTotal,
      netWorth,
      distribution,
      magnitude,
    };
  }, [wallets, filteredDebts, categories]);

  const netWorthTrend = useMemo(() => {
    if (!netWorthDistribution) return [];
    const { currentStart, currentEnd } = periodWindow;
    return getNetWorthTrend(
      fullData.expenses,
      fullData.incomes,
      filteredDebts,
      wallets,
      currentStart,
      currentEnd
    );
  }, [fullData.expenses, fullData.incomes, filteredDebts, wallets, netWorthDistribution, periodWindow]);

  const investmentTrends = useMemo(() => {
    // Mostra solo se il periodo è di almeno 1 mese
    const diffDays = differenceInCalendarDays(periodWindow.currentEnd, periodWindow.currentStart);
    if (diffDays < 28) return [];

    const types = ['Azioni', 'Obbligazioni', 'ETF'];
    const results = [];

    types.forEach(type => {
      const data = getInvestmentTrend(
        expenses,
        incomes,
        wallets,
        type,
        periodWindow.currentStart,
        periodWindow.currentEnd,
        categories
      );

      if (data.length > 0) {
        // Trova il colore dalla categoria del portafoglio
        const cat = categories?.wallet?.find(c => c.name === type) || {};
        results.push({
          type,
          data,
          color: cat.color || '#6366f1',
          icon: cat.icon || '📈'
        });
      }
    });

    return results;
  }, [expenses, incomes, wallets, periodWindow, categories]);

  const financialIndicesData = useMemo(() => {
    return getFinancialIndices(fullData.expenses, fullData.incomes, wallets, debts);
  }, [fullData.expenses, fullData.incomes, wallets, debts]);

  const expenseMonthlyTrend = useMemo(
    () =>
      monthlyData.slice(-6).map((item) => ({
        month: item.month,
        Spese: item.Spese,
      })),
    [monthlyData],
  );

  const incomeMonthlyTrend = useMemo(
    () =>
      monthlyData.slice(-6).map((item) => ({
        month: item.month,
        Entrate: item.Entrate,
      })),
    [monthlyData],
  );

  const investmentAnalysis = useMemo(() => {
    const { currentStart, currentEnd } = periodWindow;
    
    // Identifica i wallet di investimento
    const investmentWallets = new Set(
      (wallets || [])
        .filter(w => ['Azioni', 'Obbligazioni', 'ETF'].includes(w.type))
        .map(w => String(w.id))
    );

    // Filtra i dati reali per il periodo corrente includendo i trasferimenti (per gli investimenti)
    // ma rispettando i filtri attivi (per coerenza con le card superiori)
    const periodIncomesRaw = (incomes || []).filter(item => {
      const d = new Date(item.date);
      return d >= currentStart && d < currentEnd;
    });
    const periodIncomesFiltered = filterItems(periodIncomesRaw, { ...activeFilters, showTransfers: true });
    const allIncomes = [...periodIncomesFiltered, ...projectedTransactions.incomes];

    const periodExpensesRaw = (expenses || []).filter(item => {
      const d = new Date(item.date);
      return d >= currentStart && d < currentEnd;
    });
    const periodExpensesFiltered = filterItems(periodExpensesRaw, { ...activeFilters, showTransfers: true });
    const allExpenses = [...periodExpensesFiltered, ...projectedTransactions.expenses];

    // Calcolo totali
    const totalIncome = allIncomes
      .filter(item => item.category !== 'Trasferimento')
      .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

    const totalExpenses = allExpenses
      .filter(item => item.category !== 'Trasferimento')
      .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

    // Considera TUTTE le entrate nei conti di investimento (Trasferimenti, Dividendi, etc.)
    const totalInvestments = allIncomes
      .filter(item => investmentWallets.has(String(item.walletId)))
      .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

    const totalSavings = totalIncome - totalExpenses - totalInvestments;

    return {
      income: totalIncome,
      expenses: totalExpenses,
      investments: totalInvestments,
      savings: totalSavings,
      expensesPct: totalIncome > 0 ? (totalExpenses / totalIncome) * 100 : 0,
      investmentsPct: totalIncome > 0 ? (totalInvestments / totalIncome) * 100 : 0,
      savingsPct: totalIncome > 0 ? (totalSavings / totalIncome) * 100 : 0
    };
  }, [incomes, expenses, wallets, periodWindow, projectedTransactions, activeFilters]);

  const ThemedTooltip = ({ active, payload, label, labelPrefix, valueFormatter }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
          {label && (
            <p className="font-medium text-gray-900 dark:text-gray-100 mb-1">
              {labelPrefix}
              {label}
            </p>
          )}
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {valueFormatter ? valueFormatter(entry.value) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const getCategoryIcon = (categoryName) => {
    const expenseCategory = categories.expense?.find((cat) => cat.name === categoryName);
    const incomeCategory = categories.income?.find((cat) => cat.name === categoryName);
    const category = expenseCategory || incomeCategory;
    return category?.icon || '📦';
  };

  const CustomTooltip = ({ active, payload, label, valueFormatter }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
          <p className="font-medium text-gray-900 dark:text-gray-100">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {valueFormatter ? valueFormatter(entry.value) : formatCurrencyShort(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const formatMonthLabel = (monthKey) => {
    if (!monthKey) return '';
    const [year, month] = monthKey.split('-');
    const parsedDate = new Date(Number(year), Number(month) - 1, 1);
    return Number.isNaN(parsedDate.getTime())
      ? monthKey
      : format(parsedDate, "LLL ''yy", { locale: it });
  };

  const overviewCards = [
    {
      key: 'income',
      title: 'Entrate',
      value: overviewComparison.current.income,
      previous: overviewComparison.previous.income,
      accent: 'text-green-600',
      icon: <TrendingUp className="w-4 h-4" />,
    },
    {
      key: 'expense',
      title: 'Spese',
      value: overviewComparison.current.expense,
      previous: overviewComparison.previous.expense,
      accent: 'text-red-600',
      icon: <TrendingDown className="w-4 h-4" />,
    },
    {
      key: 'balance',
      title: 'Saldo',
      value: overviewComparison.current.balance,
      previous: overviewComparison.previous.balance,
      accent: 'text-blue-600',
      icon: <DollarSign className="w-4 h-4" />,
    },
  ];

  const expenseDayBreakdown = WEEK_DAYS.map((day, idx) => ({
    day,
    value: weeklyExpenseAverages[idx] || 0,
  }));

  const incomeDayBreakdown = WEEK_DAYS.map((day, idx) => ({
    day,
    value: weeklyIncomeAverages[idx] || 0,
  }));

  const netWorthColorFor = (slice, index) => {
    if (slice.color) return slice.color;
    if (slice.kind === 'credit') return CREDIT_COLOR;
    if (slice.kind === 'debt') return DEBT_COLOR;
    return DISTRIBUTION_COLORS[index % DISTRIBUTION_COLORS.length];
  };

  const todayLabel = format(getCurrentRomeTime(), "d LLL", { locale: it });



  const prevPeriodExpenses = useMemo(() => {
    const { previousStart, previousEnd } = periodWindow || {};
    if (!previousStart || !previousEnd) return [];
    return (fullData.expenses || []).filter((item) => {
      if (!item?.date) return false;
      const date = new Date(item.date);
      if (Number.isNaN(date.getTime())) return false;
      return date >= previousStart && date < previousEnd;
    });
  }, [fullData, periodWindow]);

  const prevPeriodIncomes = useMemo(() => {
    const { previousStart, previousEnd } = periodWindow || {};
    if (!previousStart || !previousEnd) return [];
    return (fullData.incomes || []).filter((item) => {
      if (!item?.date) return false;
      const date = new Date(item.date);
      if (Number.isNaN(date.getTime())) return false;
      return date >= previousStart && date < previousEnd;
    });
  }, [fullData, periodWindow]);

  const incomeRadarData = useMemo(() => {
    return getRadarData(periodData.incomes, prevPeriodIncomes);
  }, [periodData.incomes, prevPeriodIncomes]);

  const radarData = useMemo(() => {
    return getRadarData(periodData.expenses, prevPeriodExpenses);
  }, [periodData.expenses, prevPeriodExpenses]);




  return (
    <div className="space-y-4">
      <div className="card p-2">
        <div className="grid grid-cols-3 gap-1.5 text-center">
          {[
            { key: 'expense', label: 'Spese', activeClass: 'bg-red-600 text-white shadow-sm', baseClass: 'text-red-700 dark:text-red-200' },
            { key: 'general', label: 'Generale', activeClass: 'bg-blue-600 text-white shadow-sm', baseClass: 'text-blue-700 dark:text-blue-200' },
            { key: 'income', label: 'Entrate', activeClass: 'bg-green-600 text-white shadow-sm', baseClass: 'text-green-700 dark:text-green-200' }
          ].map((option) => {
            const isActive = activeSection === option.key;
            return (
              <button
                key={option.key}
                onClick={() => setActiveSection(option.key)}
                className={`w-full px-3 py-2 rounded-lg text-sm font-semibold transition-all ${isActive
                  ? option.activeClass
                  : `${option.baseClass} bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700`
                  }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {activeSection === 'general' && (
        <>
          {renderRangeControls('general')}

          <section className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              {overviewCards.map((card) => {
                let colorClasses = '';
                let titleColor = '';
                let valueColor = '';

                if (card.key === 'income') {
                  colorClasses = 'bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800/30';
                  titleColor = 'text-green-600 dark:text-green-400';
                  valueColor = 'text-green-700 dark:text-green-300';
                } else if (card.key === 'expense') {
                  colorClasses = 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/30';
                  titleColor = 'text-red-600 dark:text-red-400';
                  valueColor = 'text-red-700 dark:text-red-300';
                } else {
                  // Balance
                  colorClasses = 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800/30';
                  titleColor = 'text-blue-600 dark:text-blue-400';
                  valueColor = 'text-blue-700 dark:text-blue-300';
                }

                return (
                  <div key={card.key} className={`${colorClasses} p-3 rounded-2xl border text-center`}>
                    <div className={`text-[10px] uppercase tracking-wide font-bold ${titleColor} mb-1`}>
                      {card.title}
                    </div>
                    <div className={`text-base sm:text-lg font-bold ${valueColor} truncate`}>
                      {formatCurrencyShort(card.value)}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-7 space-y-4">
              <div className="card p-6">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Andamento entrate e spese
                  </h4>
                </div>
                {(() => {
                  if (sanitizedComparisonRange === 'year') {
                    if (yearlyChartData.some((d) => d.Entrate !== 0 || d.Spese !== 0)) {
                      return (
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={yearlyChartData} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                            <YAxis
                              tick={{ fontSize: 10 }}
                              width={60}
                              tickFormatter={(value) => formatCurrencyShort(value)}
                              {...getAxisConfig(yearlyChartData, ['Entrate', 'Spese'])}
                            />
                            <Tooltip content={<ThemedTooltip valueFormatter={formatCurrencyShort} />} />
                            <Bar dataKey="Entrate" name="Entrate" fill="#22c55e" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="Spese" name="Spese" fill="#ef4444" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      );
                    }
                    return <div className="text-center py-10 text-gray-500 dark:text-gray-400">Nessun dato disponibile per quest'anno</div>;
                  }

                  if (adaptiveTrendData.type === 'bar') {
                    return (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={adaptiveTrendData.data} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.3} vertical={false} />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                          <YAxis
                            tick={{ fontSize: 10 }}
                            width={60}
                            tickFormatter={(value) => formatCurrencyShort(value)}
                            {...getAxisConfig(adaptiveTrendData.data, ['Entrate', 'Spese'])}
                          />
                          <Tooltip content={<ThemedTooltip valueFormatter={formatCurrencyShort} />} />
                          <Bar dataKey="Entrate" name="Entrate" fill="#22c55e" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Spese" name="Spese" fill="#ef4444" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    );
                  }

                  if (adaptiveTrendData.data.length > 0) {
                    return (
                      <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={adaptiveTrendData.data} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="colorExpense" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                          <XAxis
                            dataKey="label"
                            tick={{ fontSize: 10 }}
                            minTickGap={30}
                          />
                          <YAxis
                            tick={{ fontSize: 10 }}
                            width={60}
                            tickFormatter={(value) => formatCurrencyShort(value)}
                            {...getAxisConfig(adaptiveTrendData.data, ['Entrate', 'Spese'])}
                          />
                          <Tooltip content={<ThemedTooltip valueFormatter={formatCurrencyShort} />} />
                          <ReferenceLine x={todayLabel} stroke={theme === 'dark' ? '#ffffff' : '#374151'} strokeWidth={2} />
                          <Area type="monotone" dataKey="Entrate" stroke="#22c55e" fillOpacity={0.6} fill="url(#colorIncome)" strokeWidth={2} />
                          <Area type="monotone" dataKey="Spese" stroke="#ef4444" fillOpacity={0.6} fill="url(#colorExpense)" strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    );
                  }

                  return <div className="text-center py-10 text-gray-500 dark:text-gray-400">Nessun dato disponibile</div>;
                })()}
              </div>

              {/* Nuove card percentuali */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  (() => {
                    const income = overviewComparison.current.income;
                    const balance = overviewComparison.current.balance;
                    const savingsRate = income > 0 ? (balance / income) * 100 : 0;
                    return {
                      label: '% Risparmio',
                      displayValue: `${savingsRate.toFixed(1)}%`,
                      colorClass: savingsRate >= 0 ? 'text-green-600' : 'text-red-600',
                      bgClass: 'bg-white dark:bg-gray-800'
                    };
                  })(),
                  (() => {
                    const curr = overviewComparison.current.income;
                    const prev = overviewComparison.previous.income;
                    const diff = curr - prev;
                    const percent = prev > 0 ? (diff / prev) * 100 : (curr > 0 ? 100 : 0);
                    const prefix = percent > 0 ? '+' : '';

                    return {
                      label: 'Entrate',
                      displayValue: `${prefix}${percent.toFixed(1)}%`,
                      colorClass: diff >= 0 ? 'text-green-600' : 'text-red-600',
                      bgClass: 'bg-white dark:bg-gray-800'
                    };
                  })(),
                  (() => {
                    const curr = overviewComparison.current.expense;
                    const prev = overviewComparison.previous.expense;
                    const diff = curr - prev;
                    const percent = prev > 0 ? (diff / prev) * 100 : (curr > 0 ? 100 : 0);
                    const isIncrease = diff > 0;
                    const isZero = diff === 0;
                    const prefix = percent > 0 ? '+' : '';

                    return {
                      label: 'Spese',
                      displayValue: `${prefix}${percent.toFixed(1)}%`,
                      colorClass: isIncrease ? 'text-red-600' : (isZero ? 'text-gray-600' : 'text-green-600'),
                      bgClass: 'bg-white dark:bg-gray-800'
                    };
                  })()
                ].map((item, idx) => (
                  <div key={idx} className={`card p-3 flex flex-col items-center justify-center text-center ${item.bgClass}`}>
                    <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">
                      {item.label}
                    </span>
                    <span className={`text-lg sm:text-xl font-bold ${item.colorClass} mt-1`}>
                      {item.displayValue}
                    </span>
                  </div>
                ))}
              </div>

              {/* Analisi Gestione Liquidità */}
              <div className="card p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-md font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-blue-500" />
                    Gestione Liquidità
                  </h4>
                  <div className="text-sm font-black text-emerald-500">
                    {formatCurrencyShort(investmentAnalysis.income)}
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Progress bar multi-colore */}
                  <div className="h-3 w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex shadow-inner">
                    <div 
                      className="h-full bg-red-500 transition-all duration-500" 
                      style={{ width: `${Math.max(0, Math.min(100, investmentAnalysis.expensesPct))}%` }}
                      title={`Spese: ${investmentAnalysis.expensesPct.toFixed(1)}%`}
                    />
                    <div 
                      className="h-full bg-indigo-500 transition-all duration-500" 
                      style={{ width: `${Math.max(0, Math.min(100, investmentAnalysis.investmentsPct))}%` }}
                      title={`Investimenti: ${investmentAnalysis.investmentsPct.toFixed(1)}%`}
                    />
                    <div 
                      className="h-full bg-emerald-500 transition-all duration-500" 
                      style={{ width: `${Math.max(0, Math.min(100, investmentAnalysis.savingsPct))}%` }}
                      title={`Risparmio: ${investmentAnalysis.savingsPct.toFixed(1)}%`}
                    />
                  </div>

                  {/* Legenda e Valori */}
                  {/* Legenda e Valori - Una sola riga su mobile */}
                  <div className="flex items-start justify-between gap-1 sm:gap-3">
                    <div className="flex flex-col items-center flex-1 min-w-0 text-center">
                      <div className="flex items-center gap-1 text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-tighter">
                        <div className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                        Spese
                      </div>
                      <div className="text-[11px] sm:text-sm font-black text-gray-900 dark:text-gray-100 truncate w-full">
                        {formatCurrencyShort(investmentAnalysis.expenses)}
                      </div>
                      <div className="text-[9px] font-bold text-red-500">
                        {investmentAnalysis.expensesPct.toFixed(1)}%
                      </div>
                    </div>

                    <div className="flex flex-col items-center flex-1 min-w-0 text-center border-x border-gray-100 dark:border-gray-800 px-1">
                      <div className="flex items-center gap-1 text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-tighter">
                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
                        Investimenti
                      </div>
                      <div className="text-[11px] sm:text-sm font-black text-gray-900 dark:text-gray-100 truncate w-full">
                        {formatCurrencyShort(investmentAnalysis.investments)}
                      </div>
                      <div className="text-[9px] font-bold text-indigo-500">
                        {investmentAnalysis.investmentsPct.toFixed(1)}%
                      </div>
                    </div>

                    <div className="flex flex-col items-center flex-1 min-w-0 text-center">
                      <div className="flex items-center gap-1 text-[9px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-tighter">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                        Risparmi
                      </div>
                      <div className="text-[11px] sm:text-sm font-black text-gray-900 dark:text-gray-100 truncate w-full">
                        {formatCurrencyShort(investmentAnalysis.savings)}
                      </div>
                      <div className={`text-[9px] font-bold ${investmentAnalysis.savings >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                        {investmentAnalysis.savingsPct.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-6 lg:row-span-2">
              <div className="card p-6">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <WalletIcon className="w-5 h-5" />
                    Patrimonio attuale
                  </h4>
                </div>
              {netWorthDistribution.distribution.length > 0 && netWorthDistribution.magnitude > 0 ? (
                <div className="flex flex-col gap-4 mt-4">
                  <div className="h-44 w-full relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <RePieChart>
                        <Pie
                          data={netWorthDistribution.distribution}
                          dataKey="chartValue"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={70}
                          paddingAngle={3}
                          label={({ percent }) => `${Math.round(percent * 100)}%`}
                        >
                          {netWorthDistribution.distribution.map((slice, index) => (
                            <Cell key={`${slice.name}-${index}`} fill={netWorthColorFor(slice, index)} />
                          ))}
                        </Pie>
                        <Tooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const entry = payload[0].payload;
                              return (
                                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
                                  <p className="font-medium text-gray-900 dark:text-gray-100 mb-1">{entry.name}</p>
                                  <p className="text-sm">{formatCurrency(entry.value)}</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                      </RePieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="w-full space-y-3">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Saldo totale</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {formatCurrency(netWorthDistribution.netWorth)}
                      </p>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                      {netWorthDistribution.distribution.map((slice, index) => (
                        <div key={`${slice.name}-${index}`} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                            <span
                              className="w-3 h-3 rounded-full inline-block"
                              style={{ backgroundColor: netWorthColorFor(slice, index) }}
                            ></span>
                            <span>{slice.name}</span>
                          </div>
                          <span
                            className={`font-medium ${slice.tone === 'negative'
                              ? 'text-red-600 dark:text-red-300'
                              : 'text-gray-900 dark:text-gray-100'
                              }`}
                          >
                            {formatCurrency(slice.value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                  Aggiungi conti, crediti o debiti per vedere il grafico del saldo.
                </div>
              )}

              {netWorthTrend.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Andamento patrimonio
                  </h5>
                  <div className="h-44 w-full relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={netWorthTrend} margin={{ top: 5, right: 0, left: 10, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorNetWorth" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 10 }}
                          interval="preserveStartEnd"
                          minTickGap={30}
                        />
                        <YAxis
                          domain={['auto', 'auto']}
                          tick={{ fontSize: 10 }}
                          width={45}
                          tickFormatter={(value) => {
                            if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                            if (value >= 100000) return `${(value / 1000).toFixed(0)}k`; // 100k
                            if (value >= 1000) return `${(value / 1000).toFixed(1)}k`; // 1.5k
                            return value;
                          }}
                        />
                        <Tooltip content={<ThemedTooltip valueFormatter={formatCurrencyShort} />} />
                        <Area
                          type="monotone"
                          dataKey="value"
                          name="Patrimonio"
                          stroke="#6366f1"
                          fill="url(#colorNetWorth)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
                </div>
              </div>
              <div className="lg:col-span-7">
              {/* Sezione Indici Finanziari */}
              <section className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-indigo-500" />
                    Indici Finanziari
                  </h4>
                  <div className="text-[10px] uppercase font-bold tracking-widest text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-md border border-gray-200 dark:border-gray-700">
                    Data Globale
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3">
                  <FinancialIndexCard
                    id="solidita"
                    index={{ ...financialIndicesData.solidita, label: 'Solidità', status: financialIndicesData.solidita.label }}
                    icon={ShieldCheck}
                    onClick={setSelectedFinancialIndex}
                  />
                  <FinancialIndexCard
                    id="investimenti"
                    index={{ ...financialIndicesData.investimenti, label: 'Investimenti', status: financialIndicesData.investimenti.label }}
                    icon={PieChart}
                    onClick={setSelectedFinancialIndex}
                  />
                  <FinancialIndexCard
                    id="crescita"
                    index={{ ...financialIndicesData.crescita, label: 'Crescita', status: financialIndicesData.crescita.label }}
                    icon={TrendingUp}
                    onClick={setSelectedFinancialIndex}
                  />
                  <FinancialIndexCard
                    id="risparmio"
                    index={{ ...financialIndicesData.risparmio, label: 'Risparmio', status: financialIndicesData.risparmio.label }}
                    icon={Zap}
                    onClick={setSelectedFinancialIndex}
                  />
                  <FinancialIndexCard 
                    id="diversificazione" 
                    index={{...financialIndicesData.diversificazione, label: 'Diversificazione', status: financialIndicesData.diversificazione.label}} 
                    icon={Activity} 
                    onClick={setSelectedFinancialIndex} 
                  />
                  <FinancialIndexCard
                    id="controllo"
                    index={{ ...financialIndicesData.controllo, label: 'Controllo', status: financialIndicesData.controllo.label }}
                    icon={Layers}
                    onClick={setSelectedFinancialIndex}
                  />
                  <FinancialIndexCard
                    id="reddito"
                    index={{ ...financialIndicesData.reddito, label: 'Reddito', status: financialIndicesData.reddito.label }}
                    icon={DollarSign}
                    onClick={setSelectedFinancialIndex}
                  />
                  <FinancialIndexCard
                    id="solvibilita"
                    index={{ ...financialIndicesData.solvibilita, label: 'Solvibilità', status: financialIndicesData.solvibilita.label }}
                    icon={ShieldCheck}
                    onClick={setSelectedFinancialIndex}
                  />
                </div>
              </section>

              {/* Sezione Investimenti (Azioni, Obbligazioni, ETF) */}
              {investmentTrends.length > 0 && sanitizedComparisonRange !== 'month' && sanitizedComparisonRange !== 'week' && (
                <section className="mb-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                       <PieChart className="w-5 h-5 text-purple-500" />
                       Andamento Investimenti
                    </h4>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {investmentTrends.map((trend) => (
                      <div key={trend.type} className="card p-5 space-y-4 relative overflow-hidden group">
                        <div className="flex items-center justify-between relative z-10">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shadow-sm"
                              style={{ backgroundColor: `${trend.color}15`, color: trend.color }}
                            >
                              {trend.icon}
                            </div>
                            <div>
                              <div className="text-[10px] uppercase font-black tracking-widest text-gray-500 leading-none mb-1">{trend.type}</div>
                              <div className="text-lg font-black text-gray-900 dark:text-gray-100 leading-none">
                                {formatCurrency(trend.data[trend.data.length - 1]?.total || 0)}
                              </div>
                            </div>
                          </div>

                          {(() => {
                            const lastData = trend.data[trend.data.length - 1];
                            const total = lastData?.total || 0;
                            const capital = lastData?.capital || 0;
                            if (capital <= 0) return null;
                            const variation = ((total - capital) / capital) * 100;
                            const isPositive = variation >= 0;
                            return (
                              <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold leading-none ${isPositive ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'}`}>
                                {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                {isPositive ? '+' : ''}{variation.toFixed(1)}%
                              </div>
                            );
                          })()}
                        </div>

                        <div className="h-44 w-full mt-2">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={trend.data} margin={{ top: 5, right: 5, left: 10, bottom: 0 }}>
                              <defs>
                                <linearGradient id={`grad-${trend.type}`} x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor={trend.color} stopOpacity={0.3} />
                                  <stop offset="95%" stopColor={trend.color} stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.1} />
                              <XAxis 
                                dataKey="label" 
                                hide={false} 
                                axisLine={false} 
                                tickLine={false} 
                                tick={{ fontSize: 9, fill: '#94a3b8' }}
                                interval="preserveStartEnd"
                              />
                              <YAxis 
                                hide={false} 
                                domain={['auto', 'auto']}
                                tick={{ fontSize: 9, fill: '#94a3b8' }}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={(value) => formatCurrencyShort(value)}
                                width={45}
                              />
                              <Tooltip 
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    return (
                                      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-2.5 min-w-[120px]">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">{payload[0].payload.label}</p>
                                        <div className="space-y-1">
                                          <p className="text-xs font-bold text-gray-400 flex items-center justify-between gap-4">
                                            <span>Capitale:</span>
                                            <span className="text-gray-500">{formatCurrency(payload[0].payload.capital)}</span>
                                          </p>
                                          <p className="text-sm font-black flex items-center justify-between gap-4" style={{ color: trend.color }}>
                                            <span>Valore:</span>
                                            <span>{formatCurrency(payload[0].payload.total)}</span>
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                              />
                              {/* Linea Capitale (Grigia) */}
                              <Area 
                                type="monotone" 
                                dataKey="capital" 
                                stroke="#94a3b8" 
                                fill="transparent" 
                                strokeWidth={2} 
                                strokeDasharray="4 4"
                                dot={false}
                                activeDot={{ r: 4, fill: '#94a3b8' }}
                              />
                              {/* Linea Totale (Evidente) */}
                              <Area 
                                type="monotone" 
                                dataKey="total" 
                                stroke={trend.color} 
                                fill={`url(#grad-${trend.type})`} 
                                strokeWidth={3} 
                                dot={{ r: 3, fill: trend.color, strokeWidth: 0 }}
                                activeDot={{ r: 5, fill: trend.color, stroke: '#fff', strokeWidth: 2 }}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </section>









        </>
      )}

      {activeSection === 'expense' && (
        <>
          {renderRangeControls('expense')}

          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-2xl border border-red-100 dark:border-red-800/30 text-center">
              <div className="text-[10px] uppercase tracking-wide font-bold text-red-600 dark:text-red-400 mb-1">Totale</div>
              <div className="text-base sm:text-lg font-bold text-red-700 dark:text-red-300 truncate">
                {formatCurrencyShort(expenseBreakdown.total)}
              </div>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-2xl border border-orange-100 dark:border-orange-800/30 text-center">
              <div className="text-[10px] uppercase tracking-wide font-bold text-orange-600 dark:text-orange-400 mb-1">Dirette</div>
              <div className="text-base sm:text-lg font-bold text-orange-700 dark:text-orange-300 truncate">
                {formatCurrencyShort(expenseBreakdown.direct)}
              </div>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-2xl border border-purple-100 dark:border-purple-800/30 text-center">
              <div className="text-[10px] uppercase tracking-wide font-bold text-purple-600 dark:text-purple-400 mb-1">A Debito</div>
              <div className="text-base sm:text-lg font-bold text-purple-700 dark:text-purple-300 truncate">
                {formatCurrencyShort(expenseBreakdown.debtRelated)}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="card p-5 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Confronto spese
                </h4>
              </div>
              {expenseComparisonData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={expenseComparisonData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="expenseCurrent" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10 }}
                      minTickGap={30}
                    />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      width={60}
                      tickFormatter={(value) => formatCurrencyShort(value)}
                      {...getAxisConfig(expenseComparisonData, ['current', 'previous'])}
                    />
                    <Tooltip content={<ThemedTooltip valueFormatter={formatCurrencyShort} />} />

                    <Area type="monotone" dataKey="current" name="Periodo corrente" stroke="#ef4444" fill="url(#expenseCurrent)" strokeWidth={3} activeDot={{ r: 4 }} />
                    <Area type="monotone" dataKey="previous" name="Periodo precedente" stroke="#9ca3af" fillOpacity={0.05} strokeWidth={2} strokeDasharray="4 4" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">Nessun dato disponibile</div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="card p-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-red-500" />
                  Spese per categoria
                </h4>
                {expenseCategoryChart.length > 0 ? (
                  <div className="flex flex-col gap-4">
                    <div className="h-[250px] w-full relative">
                      <ResponsiveContainer width="100%" height="100%">
                        <RePieChart>
                          <Pie
                            data={expenseCategoryChart}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={3}
                            dataKey="value"
                            label={renderCustomizedLabel}
                          >
                            {expenseCategoryChart.map((entry, idx) => (
                              <Cell key={`cell-exp-${idx}`} fill={COLORS[idx % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                        </RePieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">Nessuna spesa registrata</div>
                )}
              </div>

              <div className="card p-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-red-500" />
                  Intensità di Spesa
                </h4>
                {radarData.length > 0 ? (
                  <div className="h-[220px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                        <PolarGrid stroke={theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'} />
                        <PolarAngleAxis dataKey="subject" tick={{ fontSize: 9, fill: theme === 'dark' ? '#9ca3af' : '#4b5563', fontWeight: 600 }} />
                        <PolarRadiusAxis domain={[0, 'auto']} tick={false} axisLine={false} />
                        <Radar
                          name="Attuale"
                          dataKey="Attuale"
                          stroke="#ef4444"
                          fill="#ef4444"
                          fillOpacity={0.5}
                          strokeWidth={2}
                        />
                        <Radar
                          name="Precedente"
                          dataKey="Precedente"
                          stroke="#94a3b8"
                          fill="#94a3b8"
                          fillOpacity={0.15}
                          strokeWidth={1}
                          strokeDasharray="4 4"
                        />
                        <Tooltip content={<ThemedTooltip valueFormatter={formatCurrencyShort} />} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center py-10 text-gray-500">Dati insufficienti per l'analisi radar</div>
                )}
              </div>

              <div className="card p-6 lg:col-span-2">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <Store className="w-5 h-5 text-red-500" />
                  Spese per negozio
                </h4>
                {expenseStoreChart.length > 0 ? (
                  <div className="flex flex-col gap-4">
                    <div className="h-[300px] w-full relative">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={expenseStoreChart} layout="vertical" margin={{ top: 0, right: 50, left: 0, bottom: 0 }}>
                          <XAxis type="number" hide domain={[0, 'dataMax']} />
                          <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                          <Tooltip content={<CustomTooltip valueFormatter={formatCurrency} />} />
                          <Bar dataKey="value" fill="#ef4444" radius={[0, 4, 4, 0]}>
                            {expenseStoreChart.map((entry, idx) => (
                              <Cell key={`cell-store-exp-${idx}`} fill={COLORS[idx % COLORS.length]} />
                            ))}
                            <LabelList dataKey="value" content={({ x, y, width, height, value }) => {
                              return (
                                <text x={x + width + 5} y={y + height / 2} fill={theme === 'dark' ? '#fff' : '#000'} textAnchor="start" dominantBaseline="middle" fontSize={11}>
                                  {formatCurrency(value)}
                                </text>
                              );
                            }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">Nessuna spesa registrata</div>
                )}
              </div>
            </div>
          </div>


        </>
      )}

      {activeSection === 'income' && (
        <>
          {renderRangeControls('income')}

          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-2xl border border-green-100 dark:border-green-800/30 text-center">
              <div className="text-[10px] uppercase tracking-wide font-bold text-green-600 dark:text-green-400 mb-1">Totale</div>
              <div className="text-base sm:text-lg font-bold text-green-700 dark:text-green-300 truncate">
                {formatCurrencyShort(incomeBreakdown.total)}
              </div>
            </div>
            <div className="bg-teal-50 dark:bg-teal-900/20 p-3 rounded-2xl border border-teal-100 dark:border-teal-800/30 text-center">
              <div className="text-[10px] uppercase tracking-wide font-bold text-teal-600 dark:text-teal-400 mb-1">Dirette</div>
              <div className="text-base sm:text-lg font-bold text-teal-700 dark:text-teal-300 truncate">
                {formatCurrencyShort(incomeBreakdown.direct)}
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-2xl border border-blue-100 dark:border-blue-800/30 text-center">
              <div className="text-[10px] uppercase tracking-wide font-bold text-blue-600 dark:text-blue-400 mb-1">A Credito</div>
              <div className="text-base sm:text-lg font-bold text-blue-700 dark:text-blue-300 truncate">
                {formatCurrencyShort(incomeBreakdown.debtRelated)}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="card p-5 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  Confronto entrate
                </h4>
              </div>
              {incomeComparisonData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={incomeComparisonData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="incomeCurrent" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10 }}
                      minTickGap={30}
                    />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      width={60}
                      tickFormatter={(value) => formatCurrencyShort(value)}
                      {...getAxisConfig(incomeComparisonData, ['current', 'previous'])}
                    />
                    <Tooltip content={<ThemedTooltip valueFormatter={formatCurrencyShort} />} />

                    <Area type="monotone" dataKey="current" name="Periodo corrente" stroke="#22c55e" fill="url(#incomeCurrent)" strokeWidth={3} activeDot={{ r: 4 }} />
                    <Area type="monotone" dataKey="previous" name="Periodo precedente" stroke="#9ca3af" fillOpacity={0.05} strokeWidth={2} strokeDasharray="4 4" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">Nessun dato disponibile</div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="card p-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-green-600" />
                  Entrate per categoria
                </h4>
                {incomeCategoryChart.length > 0 ? (
                  <div className="flex flex-col gap-4">
                    <div className="h-[250px] w-full relative">
                      <ResponsiveContainer width="100%" height="100%">
                        <RePieChart>
                          <Pie
                            data={incomeCategoryChart}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={3}
                            dataKey="value"
                            label={renderCustomizedLabel}
                          >
                            {incomeCategoryChart.map((entry, idx) => (
                              <Cell key={`cell-inc-${idx}`} fill={COLORS[idx % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                        </RePieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">Nessuna entrata registrata</div>
                )}
              </div>

              <div className="card p-6">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-green-600" />
                  Intensità di Entrata
                </h4>
                {incomeRadarData.length > 0 ? (
                  <div className="h-[220px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart cx="50%" cy="50%" outerRadius="80%" data={incomeRadarData}>
                        <PolarGrid stroke={theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'} />
                        <PolarAngleAxis dataKey="subject" tick={{ fontSize: 9, fill: theme === 'dark' ? '#9ca3af' : '#4b5563', fontWeight: 600 }} />
                        <PolarRadiusAxis domain={[0, 'auto']} tick={false} axisLine={false} />
                        <Radar
                          name="Attuale"
                          dataKey="Attuale"
                          stroke="#10b981"
                          fill="#10b981"
                          fillOpacity={0.5}
                          strokeWidth={2}
                        />
                        <Radar
                          name="Precedente"
                          dataKey="Precedente"
                          stroke="#94a3b8"
                          fill="#94a3b8"
                          fillOpacity={0.15}
                          strokeWidth={1}
                          strokeDasharray="4 4"
                        />
                        <Tooltip content={<ThemedTooltip valueFormatter={formatCurrencyShort} />} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center py-10 text-gray-500">Dati insufficienti per l'analisi radar</div>
                )}
              </div>

              <div className="card p-6 lg:col-span-2">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                  <Store className="w-5 h-5 text-green-600" />
                  Entrate per fonte
                </h4>
                {incomeStoreChart.length > 0 ? (
                  <div className="flex flex-col gap-4">
                    <div className="h-[300px] w-full relative">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={incomeStoreChart} layout="vertical" margin={{ top: 0, right: 50, left: 0, bottom: 0 }}>
                          <XAxis type="number" hide domain={[0, 'dataMax']} />
                          <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                          <Tooltip content={<CustomTooltip valueFormatter={formatCurrency} />} />
                          <Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]}>
                            {incomeStoreChart.map((entry, idx) => (
                              <Cell key={`cell-store-inc-${idx}`} fill={COLORS[idx % COLORS.length]} />
                            ))}
                            <LabelList dataKey="value" content={({ x, y, width, height, value }) => {
                              return (
                                <text x={x + width + 5} y={y + height / 2} fill={theme === 'dark' ? '#fff' : '#000'} textAnchor="start" dominantBaseline="middle" fontSize={11}>
                                  {formatCurrency(value)}
                                </text>
                              );
                            }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">Nessuna entrata registrata</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {/* Modal Dettagli Indice */}
      {selectedFinancialIndex && (
        <FinancialIndexDetail
          id={selectedFinancialIndex}
          index={{
            ...financialIndicesData[selectedFinancialIndex],
            label: selectedFinancialIndex === 'solidita' ? 'Solidità' :
                   selectedFinancialIndex === 'investimenti' ? 'Investimenti' :
                   selectedFinancialIndex === 'crescita' ? 'Crescita' :
                   selectedFinancialIndex === 'risparmio' ? 'Risparmio' :
                   selectedFinancialIndex === 'stabilita' ? 'Stabilità' :
                   selectedFinancialIndex === 'controllo' ? 'Controllo' :
                   selectedFinancialIndex === 'reddito' ? 'Reddito' : 'Solvibilità',
            status: financialIndicesData[selectedFinancialIndex].label
          }}
          icon={
            selectedFinancialIndex === 'solidita' ? ShieldCheck :
            selectedFinancialIndex === 'investimenti' ? PieChart :
            selectedFinancialIndex === 'crescita' ? TrendingUp :
            selectedFinancialIndex === 'risparmio' ? Zap :
            selectedFinancialIndex === 'stabilita' ? Activity :
            selectedFinancialIndex === 'controllo' ? Layers :
            selectedFinancialIndex === 'reddito' ? DollarSign : ShieldCheck
          }
          onClose={() => setSelectedFinancialIndex(null)}
        />
      )}
    </div>
  );
}

export default Statistics;
